import * as THREE from 'three';
import { compatibilitySlotIdForAnchor, queryBuildableSubcell } from '../spatial/buildableArea';
import { FOUNDATION_DIVISION, createSubgridSpec, worldToSubgrid, type SubgridCoord } from '../spatial/subgrid';
import {
  CELL_SIZE,
  resolveLockedLandHit,
  type EntityRenderSpec,
  type MigrationRenderModel,
  type SlotRenderSpec,
} from './mapParkState';

const PICK_SPEC = createSubgridSpec(FOUNDATION_DIVISION);

export interface SceneHit {
  slotId: string;
  entityId?: string;
  occupied: boolean;
  /** Present when the hit is a canvas subcell rather than a legacy slot center. */
  anchor?: SubgridCoord;
}

/** Ignore synthesized mouse events that follow a touch, and ignore accidental double-fires. */
export const TOUCH_MOUSE_COMPAT_MS = 700;
export const SCENE_POINTER_DEBOUNCE_MS = 40;

export interface PointerGateState {
  lastAcceptedPointerType: string | null;
  lastAcceptedPointerId: number | null;
  lastAcceptedAtMs: number;
}

export function createPointerGateState(): PointerGateState {
  return {
    lastAcceptedPointerType: null,
    lastAcceptedPointerId: null,
    lastAcceptedAtMs: 0,
  };
}

export function shouldAcceptScenePointer(
  event: Pick<PointerEvent, 'button' | 'isPrimary' | 'pointerType' | 'pointerId'>,
  state: PointerGateState,
  nowMs: number,
): boolean {
  const pointerType = event.pointerType || 'mouse';
  if (pointerType === 'mouse' && event.button !== 0) {
    return false;
  }
  if (event.isPrimary === false) {
    return false;
  }
  if (nowMs - state.lastAcceptedAtMs < SCENE_POINTER_DEBOUNCE_MS) {
    return false;
  }
  if (
    pointerType === 'mouse' &&
    state.lastAcceptedPointerType === 'touch' &&
    nowMs - state.lastAcceptedAtMs < TOUCH_MOUSE_COMPAT_MS
  ) {
    return false;
  }
  if (
    event.pointerId === state.lastAcceptedPointerId &&
    nowMs - state.lastAcceptedAtMs < TOUCH_MOUSE_COMPAT_MS &&
    pointerType !== state.lastAcceptedPointerType
  ) {
    return false;
  }
  return true;
}

export function rememberAcceptedPointer(
  state: PointerGateState,
  event: Pick<PointerEvent, 'pointerType' | 'pointerId'>,
  nowMs: number,
): void {
  state.lastAcceptedPointerType = event.pointerType || 'mouse';
  state.lastAcceptedPointerId = event.pointerId;
  state.lastAcceptedAtMs = nowMs;
}

/** One accepted commit per gated pointer. Rejects hover-less duplicates and touch-mouse echoes. */
export function acceptPlacementCommit(
  event: Pick<PointerEvent, 'button' | 'isPrimary' | 'pointerType' | 'pointerId'>,
  state: PointerGateState,
  nowMs: number,
): boolean {
  if (!shouldAcceptScenePointer(event, state, nowMs)) {
    return false;
  }
  rememberAcceptedPointer(state, event, nowMs);
  return true;
}

export const PINCH_TRAIL_SUPPRESS_MS = TOUCH_MOUSE_COMPAT_MS;
/** First-touch grace window so a second finger can form a pinch with zero scene side effects. */
export const PINCH_INTENT_WINDOW_MS = 100;
/** Idle one-finger movement above this cancels the pending tap and starts camera pan. */
export const CAMERA_PAN_DRAG_THRESHOLD_PX = 12;

export interface TouchIntentState {
  pointerId: number | null;
  downAtMs: number;
  cancelled: boolean;
  flushed: boolean;
  startX: number;
  startY: number;
}

export function createTouchIntentState(): TouchIntentState {
  return {
    pointerId: null,
    downAtMs: 0,
    cancelled: false,
    flushed: false,
    startX: 0,
    startY: 0,
  };
}

export function isTouchScenePointer(event: Pick<PointerEvent, 'pointerType'>): boolean {
  return (event.pointerType || 'mouse') === 'touch';
}

export function hasPendingTouchIntent(state: TouchIntentState): boolean {
  return state.pointerId !== null && !state.cancelled && !state.flushed;
}

function armPointerIntent(
  state: TouchIntentState,
  event: Pick<PointerEvent, 'pointerId' | 'clientX' | 'clientY'>,
  nowMs: number,
): true {
  state.pointerId = event.pointerId;
  state.downAtMs = nowMs;
  state.cancelled = false;
  state.flushed = false;
  state.startX = event.clientX;
  state.startY = event.clientY;
  return true;
}

export function beginTouchIntent(
  state: TouchIntentState,
  event: Pick<PointerEvent, 'pointerType' | 'pointerId' | 'clientX' | 'clientY'>,
  nowMs: number,
): boolean {
  if (!isTouchScenePointer(event)) {
    return false;
  }
  return armPointerIntent(state, event, nowMs);
}

/** Idle desktop left-down: pending click until pointerup, or pan once movement crosses the threshold. */
export function beginIdleMouseClickIntent(
  state: TouchIntentState,
  event: Pick<PointerEvent, 'pointerType' | 'pointerId' | 'clientX' | 'clientY' | 'button' | 'shiftKey'>,
  nowMs: number,
): boolean {
  if ((event.pointerType || 'mouse') !== 'mouse') {
    return false;
  }
  if (event.button !== 0 || event.shiftKey === true) {
    return false;
  }
  return armPointerIntent(state, event, nowMs);
}

export function touchIntentDragDistancePx(
  state: TouchIntentState,
  clientX: number,
  clientY: number,
): number {
  if (state.pointerId === null) {
    return 0;
  }
  return Math.hypot(clientX - state.startX, clientY - state.startY);
}

export function isIdleCameraPanMode(flags: {
  buildMode?: boolean;
  floorMode?: boolean;
  landMode?: boolean;
  placementActive?: boolean;
}): boolean {
  return !flags.buildMode && !flags.floorMode && !flags.landMode && !flags.placementActive;
}

/**
 * Touch one-finger camera pan.
 * Idle pan stays allowed. Build browsing may pan before a placement draft.
 * Floor, Land, and an active placement draft keep their own one-finger gestures.
 * Desktop left-drag pan stays on isIdleCameraPanMode so Build clicks stay immediate.
 */
export function isOneFingerCameraPanAllowed(flags: {
  buildMode?: boolean;
  floorMode?: boolean;
  landMode?: boolean;
  placementActive?: boolean;
}): boolean {
  // buildMode does not block: browsing Build may pan until placement starts.
  return !flags.floorMode && !flags.landMode && !flags.placementActive;
}

export function shouldStartIdleOneFingerPan(
  state: TouchIntentState,
  clientX: number,
  clientY: number,
  idleMode: boolean,
  thresholdPx = CAMERA_PAN_DRAG_THRESHOLD_PX,
): boolean {
  return (
    idleMode &&
    hasPendingTouchIntent(state) &&
    touchIntentDragDistancePx(state, clientX, clientY) >= thresholdPx
  );
}

export function isDesktopCameraPanPointer(
  event: Pick<PointerEvent, 'pointerType' | 'button' | 'shiftKey'>,
): boolean {
  if ((event.pointerType || 'mouse') !== 'mouse') {
    return false;
  }
  if (event.button === 1) {
    return true;
  }
  return event.button === 0 && event.shiftKey === true;
}

export function isDesktopCameraPanHeld(
  event: Pick<PointerEvent, 'pointerType' | 'buttons' | 'shiftKey'>,
): boolean {
  if ((event.pointerType || 'mouse') !== 'mouse') {
    return false;
  }
  if ((event.buttons & 4) !== 0) {
    return true;
  }
  return (event.buttons & 1) !== 0 && event.shiftKey === true;
}

export function cancelTouchIntent(state: TouchIntentState): void {
  if (state.pointerId === null && !state.cancelled) {
    return;
  }
  state.cancelled = true;
  state.pointerId = null;
}

export function cancelTouchIntentForPinch(state: TouchIntentState, pinchActivated: boolean): boolean {
  if (!pinchActivated || !hasPendingTouchIntent(state)) {
    return false;
  }
  cancelTouchIntent(state);
  return true;
}

export function takeFlushableTouchIntent(
  state: TouchIntentState,
  pointerId: number | null,
  nowMs: number,
  reason: 'timeout' | 'pointerup',
): boolean {
  if (!hasPendingTouchIntent(state)) {
    return false;
  }
  if (pointerId !== null && state.pointerId !== pointerId) {
    return false;
  }
  if (reason === 'timeout' && nowMs - state.downAtMs < PINCH_INTENT_WINDOW_MS) {
    return false;
  }
  state.flushed = true;
  state.pointerId = null;
  return true;
}

interface PinchPointer {
  x: number;
  y: number;
}

export interface PinchGestureState {
  pointers: Map<number, PinchPointer>;
  active: boolean;
  lastDistance: number;
  lastCentroidX: number;
  lastCentroidY: number;
  endedAtMs: number;
}

export function createPinchGestureState(): PinchGestureState {
  return {
    pointers: new Map(),
    active: false,
    lastDistance: 0,
    lastCentroidX: 0,
    lastCentroidY: 0,
    endedAtMs: 0,
  };
}

function pinchDistance(state: PinchGestureState): number {
  const points = [...state.pointers.values()];
  if (points.length < 2) {
    return 0;
  }
  return Math.hypot(points[0]!.x - points[1]!.x, points[0]!.y - points[1]!.y);
}

function pinchCentroid(state: PinchGestureState): { x: number; y: number } | null {
  const points = [...state.pointers.values()];
  if (points.length === 0) {
    return null;
  }
  let x = 0;
  let y = 0;
  for (const point of points) {
    x += point.x;
    y += point.y;
  }
  return { x: x / points.length, y: y / points.length };
}

export function isPinchActive(state: PinchGestureState): boolean {
  return state.active && state.pointers.size >= 2;
}

export function isPinchSuppressingSceneAction(state: PinchGestureState, nowMs: number): boolean {
  if (isPinchActive(state)) {
    return true;
  }
  return state.endedAtMs > 0 && nowMs - state.endedAtMs < PINCH_TRAIL_SUPPRESS_MS;
}

export function canApplyDeferredSceneAction(
  intent: TouchIntentState,
  pinch: PinchGestureState,
  nowMs: number,
): boolean {
  if (isPinchSuppressingSceneAction(pinch, nowMs)) {
    return false;
  }
  return !hasPendingTouchIntent(intent);
}

export function notePinchPointerDown(
  state: PinchGestureState,
  event: Pick<PointerEvent, 'pointerType' | 'pointerId' | 'clientX' | 'clientY'>,
): { activated: boolean } {
  if ((event.pointerType || 'mouse') !== 'touch') {
    return { activated: false };
  }
  state.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
  if (state.pointers.size < 2) {
    return { activated: false };
  }
  const activated = !state.active;
  state.active = true;
  state.lastDistance = pinchDistance(state);
  const centroid = pinchCentroid(state);
  if (centroid) {
    state.lastCentroidX = centroid.x;
    state.lastCentroidY = centroid.y;
  }
  return { activated };
}

export function notePinchPointerMove(
  state: PinchGestureState,
  event: Pick<PointerEvent, 'pointerId' | 'clientX' | 'clientY'>,
): {
  distanceScale: number;
  previousCentroid: { x: number; y: number };
  nextCentroid: { x: number; y: number };
} | null {
  const existing = state.pointers.get(event.pointerId);
  if (!existing) {
    return null;
  }
  existing.x = event.clientX;
  existing.y = event.clientY;
  if (!isPinchActive(state)) {
    return null;
  }
  const next = pinchDistance(state);
  const previous = state.lastDistance;
  const nextCentroid = pinchCentroid(state);
  if (!nextCentroid) {
    return null;
  }
  const previousCentroid = { x: state.lastCentroidX, y: state.lastCentroidY };
  state.lastDistance = next > 0 ? next : previous;
  state.lastCentroidX = nextCentroid.x;
  state.lastCentroidY = nextCentroid.y;
  return {
    distanceScale: previous > 0 && next > 0 ? next / previous : 1,
    previousCentroid,
    nextCentroid,
  };
}

export function notePinchPointerUp(
  state: PinchGestureState,
  event: Pick<PointerEvent, 'pointerId'>,
  nowMs: number,
): { deactivated: boolean } {
  if (!state.pointers.delete(event.pointerId)) {
    return { deactivated: false };
  }
  if (state.active && state.pointers.size < 2) {
    state.active = false;
    state.lastDistance = 0;
    state.endedAtMs = nowMs;
    state.pointers.clear();
    return { deactivated: true };
  }
  return { deactivated: false };
}

export function pinchZoomScaleDirection(previousDistance: number, nextDistance: number): -1 | 0 | 1 {
  if (previousDistance <= 0 || nextDistance <= 0) {
    return 0;
  }
  if (nextDistance > previousDistance) {
    return 1;
  }
  if (nextDistance < previousDistance) {
    return -1;
  }
  return 0;
}

/** Meshes that must never steal prop/attraction selection. */
export const NON_SELECTABLE_PICK_VISUALS = new Set([
  'pathPatrol',
  'queue',
  'selection',
  'preview',
  'pathFloor',
]);

type MigrationPickData = {
  entityId?: string;
  visual?: string;
  slotId?: string;
};

function pointerNdc(event: PointerEvent, canvas: HTMLCanvasElement): THREE.Vector2 | null {
  const rect = canvas.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    return null;
  }
  return new THREE.Vector2(
    ((event.clientX - rect.left) / rect.width) * 2 - 1,
    -((event.clientY - rect.top) / rect.height) * 2 + 1,
  );
}

function pointerRaycaster(camera: THREE.Camera, event: PointerEvent, canvas: HTMLCanvasElement): THREE.Raycaster | null {
  const ndc = pointerNdc(event, canvas);
  if (!ndc) {
    return null;
  }
  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(ndc, camera);
  return raycaster;
}

function migrationPickData(object: THREE.Object3D): MigrationPickData | undefined {
  let current: THREE.Object3D | null = object;
  while (current) {
    const migration = current.userData.migration as MigrationPickData | undefined;
    if (migration) {
      return migration;
    }
    current = current.parent;
  }
  return undefined;
}

/** Walk ancestors for explicit selectable entity metadata. */
export function selectableEntityIdFromObject(object: THREE.Object3D): string | null {
  const migration = migrationPickData(object);
  if (!migration?.entityId) {
    return null;
  }
  if (migration.visual && NON_SELECTABLE_PICK_VISUALS.has(migration.visual)) {
    return null;
  }
  return migration.entityId;
}

export function pickWorldOnParkPlane(
  camera: THREE.Camera,
  event: PointerEvent,
  canvas: HTMLCanvasElement,
  parkTop: number,
): THREE.Vector3 | null {
  const raycaster = pointerRaycaster(camera, event, canvas);
  if (!raycaster) {
    return null;
  }
  const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -parkTop);
  const hit = new THREE.Vector3();
  if (!raycaster.ray.intersectPlane(plane, hit)) {
    return null;
  }
  return hit;
}

/** Screen-space drag to world XZ pan so the park-plane point stays under the pointer. */
export function worldPanFromClientDelta(
  camera: THREE.Camera,
  canvas: HTMLCanvasElement,
  parkTop: number,
  fromClientX: number,
  fromClientY: number,
  toClientX: number,
  toClientY: number,
): { x: number; z: number } | null {
  const from = pickWorldOnParkPlane(
    camera,
    { clientX: fromClientX, clientY: fromClientY } as PointerEvent,
    canvas,
    parkTop,
  );
  const to = pickWorldOnParkPlane(
    camera,
    { clientX: toClientX, clientY: toClientY } as PointerEvent,
    canvas,
    parkTop,
  );
  if (!from || !to) {
    return null;
  }
  return { x: from.x - to.x, z: from.z - to.z };
}

/** First rendered selectable entity along the pointer ray, skipping visitors/floors/preview/queue. */
export function pickSelectableEntityIdFromPointer(
  camera: THREE.Camera,
  event: PointerEvent,
  canvas: HTMLCanvasElement,
  scene: THREE.Object3D,
): string | null {
  const raycaster = pointerRaycaster(camera, event, canvas);
  if (!raycaster) {
    return null;
  }
  const hits = raycaster.intersectObject(scene, true);
  for (const hit of hits) {
    const entityId = selectableEntityIdFromObject(hit.object);
    if (entityId) {
      return entityId;
    }
  }
  return null;
}

function nearestSlot(model: MigrationRenderModel, x: number, z: number, maxDist: number): SlotRenderSpec | null {
  let best: SlotRenderSpec | null = null;
  let bestDist = maxDist;
  for (const slot of model.slots) {
    const dist = Math.hypot(slot.x - x, slot.z - z);
    if (dist <= bestDist) {
      bestDist = dist;
      best = slot;
    }
  }
  return best;
}

function nearestEntity(model: MigrationRenderModel, x: number, z: number, maxDist: number): EntityRenderSpec | null {
  let best: EntityRenderSpec | null = null;
  let bestDist = maxDist;
  for (const entity of model.entities) {
    const dist = Math.hypot(entity.x - x, entity.z - z);
    if (dist <= bestDist) {
      bestDist = dist;
      best = entity;
    }
  }
  return best;
}

/** Resolve a pointer hit on the park plane back to a real unlocked slot. */
export function pickSlotFromPointer(
  camera: THREE.Camera,
  event: PointerEvent,
  canvas: HTMLCanvasElement,
  model: MigrationRenderModel,
  parkTop: number,
): SlotRenderSpec | null {
  const hit = pickWorldOnParkPlane(camera, event, canvas, parkTop);
  if (!hit) {
    return null;
  }
  return nearestSlot(model, hit.x, hit.z, CELL_SIZE * 0.55);
}

export function resolveCanvasBuildHit(
  model: MigrationRenderModel,
  x: number,
  z: number,
): { slotId: string; anchor: SubgridCoord; buildable: boolean; reserved: boolean; locked: boolean } | null {
  const coord = worldToSubgrid(x, z, PICK_SPEC);
  const unlocked = model.lands.filter((land) => land.unlocked).map((land) => land.parcelId);
  const query = queryBuildableSubcell(coord, unlocked);
  if (!query.insideUsableGrass) {
    return null;
  }
  return {
    slotId: compatibilitySlotIdForAnchor(coord),
    anchor: { sx: coord.sx, sz: coord.sz },
    buildable: query.buildable,
    reserved: query.reserved,
    locked: !query.unlocked && !query.reserved,
  };
}

/** Ground/slot/locked-land fallback used when no selectable entity mesh is hit. */
export function resolveGroundSceneHit(model: MigrationRenderModel, x: number, z: number): SceneHit | null {
  const slot = nearestSlot(model, x, z, CELL_SIZE * 0.55);
  if (slot) {
    return {
      slotId: slot.slotId,
      occupied: slot.occupied,
    };
  }
  const locked = resolveLockedLandHit(model, x, z);
  if (!locked) {
    return null;
  }
  return { slotId: locked.slotId, occupied: false };
}

/** Map a park-plane world point to an unlocked slot and, when present, its authoritative entity. */
export function resolveSceneHit(model: MigrationRenderModel, x: number, z: number): SceneHit | null {
  const entity = nearestEntity(model, x, z, CELL_SIZE * 0.7);
  if (entity) {
    return { slotId: entity.slotId, entityId: entity.id, occupied: true };
  }
  return resolveGroundSceneHit(model, x, z);
}

export function pickSubcellFromPointer(
  camera: THREE.Camera,
  event: PointerEvent,
  canvas: HTMLCanvasElement,
  parkTop: number,
): SubgridCoord | null {
  const hit = pickWorldOnParkPlane(camera, event, canvas, parkTop);
  if (!hit) {
    return null;
  }
  return worldToSubgrid(hit.x, hit.z, PICK_SPEC);
}

/** Resolve a pointer hit to a buildable slot or a rendered entity already in authoritative state. */
export function pickSceneHitFromPointer(
  camera: THREE.Camera,
  event: PointerEvent,
  canvas: HTMLCanvasElement,
  model: MigrationRenderModel,
  parkTop: number,
  scene?: THREE.Object3D,
): SceneHit | null {
  if (scene) {
    const entityId = pickSelectableEntityIdFromPointer(camera, event, canvas, scene);
    if (entityId) {
      const entity = model.entities.find((item) => item.id === entityId);
      if (entity) {
        return { slotId: entity.slotId, entityId: entity.id, occupied: true };
      }
    }
    const ground = pickWorldOnParkPlane(camera, event, canvas, parkTop);
    if (!ground) {
      return null;
    }
    const canvasHit = resolveCanvasBuildHit(model, ground.x, ground.z);
    if (canvasHit) {
      return {
        slotId: canvasHit.slotId,
        occupied: false,
        anchor: canvasHit.anchor,
      };
    }
    return resolveGroundSceneHit(model, ground.x, ground.z);
  }
  const hit = pickWorldOnParkPlane(camera, event, canvas, parkTop);
  if (!hit) {
    return null;
  }
  return resolveSceneHit(model, hit.x, hit.z);
}
